import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { map, startWith } from "rxjs";

import { formatDate } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { MatTooltipModule } from "@angular/material/tooltip";

import { IotnetGateway } from "../../../apis/stats/rest/models";
import { TimeAgoPipe } from "../../../helpers/datehelper/time-ago.pipe";
import { StatusIcon } from "../../common/status-icon/status-icon.component";

/**
 * Presentational caption for a gateway: status icon, name, id/tenant and a
 * relative "last seen" time (exact date on hover). Shared between the detail
 * overlay and the map marker tooltips.
 */
@Component({
  selector: "iotnet-gateway-caption",
  templateUrl: "iotnet-gateway-caption.html",
  styleUrls: ["./iotnet-gateway-caption.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatTooltipModule, StatusIcon, TimeAgoPipe, TranslatePipe],
})
export class IotnetGatewayCaption {
  private readonly translate = inject(TranslateService);

  public readonly gateway = input.required<IotnetGateway>();
  public readonly status = input.required<string>();

  protected readonly lastSeen = computed<Date | null>(() => {
    const unix = this.gateway().last_seen;
    return unix ? new Date(unix * 1000) : null;
  });

  /** Locale for the relative time, following the active translation language. */
  protected readonly locale = toSignal(
    this.translate.onLangChange.pipe(
      map((e) => e.lang),
      // No language is set before app initialization (and in tests).
      startWith(this.translate.getCurrentLang() ?? "en"),
    ),
    { requireSync: true },
  );

  /** Exact timestamp shown as a tooltip on the relative time. */
  protected readonly lastSeenExact = computed(() => {
    const seen = this.lastSeen();
    return seen ? formatDate(seen, "d MMMM y, HH:mm", this.locale()) : "";
  });
}
